import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BillingCycle, ChargeType, DiscountType } from 'src/app/shared/enums/enums';
import {
  AddonState,
  AppAccessState,
  PricingTier,
  SubscriptionPackageState,
} from '../../../features/admin-portal/modules/company-management/components/add-company-v2/add-company-v2-state.model';
import { AppLiteState } from '../../../features/admin-portal/modules/subscription/models/subscription.models';

/**
 * Shared component for displaying the currently active subscription plan.
 * Shows plan details, pricing, seats, and action buttons for management.
 *
 * Used in:
 * - Manage Subscription page
 * - Company details view
 * - Subscription dashboard
 */
@Component({
  selector: 'app-active-subscription-plan',
  templateUrl: './active-subscription-plan.component.html',
  styleUrls: ['./active-subscription-plan.component.css'],
  standalone: false,
})
export class ActiveSubscriptionPlanComponent {
  // ============================================================================
  // INPUT PROPERTIES (Configuration from parent components)
  // ============================================================================

  /** The current active subscription package to display */
  @Input() activeSubscriptionPlan: SubscriptionPackageState | null = null;

  /** The current billing cycle of the subscription */
  @Input() currentBillingCycle: BillingCycle = BillingCycle.Monthly;

  /** Whether the component is in a loading state */
  @Input() isLoading: boolean = false;

  /** Whether to show action buttons (Cancel, Change Plan) */
  @Input() showActionButtons: boolean = true;

  /** Whether to allow cancelling the subscription */
  @Input() allowCancellation: boolean = true;

  /** Whether to allow changing the subscription plan */
  @Input() allowPlanChange: boolean = true;

  /** Whether to show "Select Plan" option when no active subscription */
  @Input() allowPlanSelection: boolean = true;

  /** Whether the cancel operation is in progress */
  @Input() isCancellingInProgress: boolean = false;

  /** Custom text for the cancel button */
  @Input() cancelButtonText: string = 'Cancel Plan';

  /** Custom text for the change plan button */
  @Input() changePlanButtonText: string = 'Change Plan';

  /** Whether to show detailed pricing breakdown */
  @Input() showDetailedPricingBreakdown: boolean = true;

  /** Whether to show the status badge */
  @Input() showStatusBadge: boolean = true;

  /** Current subscription status */
  @Input() subscriptionStatus: number = 1; // Default to Active

  /** Optional: List of available applications with full details including icons */
  @Input() availableApplications: AppLiteState[] = [];

  /** Optional: Trial end date (ISO date string) for trial subscriptions */
  @Input() trialEndDate: string | null = null;

  /** Label shown above the plan name (e.g. "Current Plan", "Upcoming Plan") */
  @Input() planLabel: string = 'Current Plan';

  /** For scheduled/upcoming plans: the date the plan becomes active */
  @Input() scheduledStartDate: string | null = null;

  /** Whether there is a scheduled/upcoming plan change pending */
  @Input() hasScheduledPlan: boolean = false;

  /**
   * The subscription package ID of the scheduled/upcoming plan.
   * Used to distinguish between a plan change and an add-on update:
   * - Same as activeSubscriptionPlan.id → add-on update scheduled
   * - Different → plan change scheduled
   */
  @Input() scheduledPlanPackageId: number | null = null;

  /**
   * Returns true when the scheduled update only changes add-ons (same plan package).
   * Returns false when it is a full plan change (different package ID).
   */
  get isAddOnUpdateScheduled(): boolean {
    if (!this.hasScheduledPlan || this.scheduledPlanPackageId == null) return false;
    return this.scheduledPlanPackageId === this.activeSubscriptionPlan?.id;
  }

  /** Whether the subscription is scheduled for cancellation at end of cycle */
  @Input() isCancellationScheduled: boolean = false;

  /** The date on which the subscription will be cancelled (plan end date) */
  @Input() cancellationDate: string | null = null;

  /** Whether to allow managing add-ons (admin-only feature) */
  @Input() allowManageAddOns: boolean = false;

  // ============================================================================
  // ENUM ACCESS (For template)
  // ============================================================================
  readonly SubscriptionStatus = {
    Unknown: 0,
    Active: 1,
    Trial: 2,
    PastDue: 3,
    Suspended: 4,
    Expired: 5,
    Cancelled: 6,
  };

  // ============================================================================
  // OUTPUT EVENTS (Communication to parent components)
  // ============================================================================

  /** Emitted when the user clicks the Cancel Plan button */
  @Output() cancelPlanRequested = new EventEmitter<void>();

  /** Emitted when the user clicks the Change Plan button */
  @Output() changePlanRequested = new EventEmitter<void>();

  /** Emitted when the user clicks to view more details */
  @Output() viewDetailsRequested = new EventEmitter<void>();

  /** Emitted when the user clicks the View Scheduled Plan button */
  @Output() viewScheduledPlanRequested = new EventEmitter<void>();

  /** Emitted when the user clicks the Manage Add-ons button */
  @Output() manageAddOnsRequested = new EventEmitter<void>();

  // ============================================================================
  // ENUMS (exposed for template access)
  // ============================================================================

  readonly BillingCycle = BillingCycle;
  readonly ChargeType = ChargeType;

  // ============================================================================
  // COMPONENT METHODS
  // ============================================================================

  /**
   * Handles the cancel plan button click
   */
  onCancelPlanClick(): void {
    if (this.canShowCancelButton() && !this.isCancellingInProgress) {
      this.cancelPlanRequested.emit();
    }
  }

  /**
   * Handles the change plan button click
   */
  onChangePlanClick(): void {
    if (this.canShowChangePlanButton()) {
      this.changePlanRequested.emit();
    }
  }

  /**
   * Handles the Manage Add-ons button click
   */
  onManageAddOnsClick(): void {
    if (this.canShowManageAddOnsButton()) {
      this.manageAddOnsRequested.emit();
    }
  }

  /**
   * Determines if the Cancel button should be shown based on subscription status
   * Cancel button available for Active, Trial, and PastDue subscriptions
   */
  canShowCancelButton(): boolean {
    if (!this.allowCancellation || !this.showActionButtons) {
      return false;
    }

    // Only allow cancellation for Active, Trial, or PastDue subscriptions
    return (
      this.subscriptionStatus === this.SubscriptionStatus.Active ||
      this.subscriptionStatus === this.SubscriptionStatus.Trial ||
      this.subscriptionStatus === this.SubscriptionStatus.PastDue
    );
  }

  /**
   * Determines if the Change Plan button should be shown based on subscription status
   * Change Plan button available for all statuses (to allow reactivation/renewal)
   */
  canShowChangePlanButton(): boolean {
    if (!this.allowPlanChange || !this.showActionButtons) {
      return false;
    }

    // Always allow changing plan (for renewal, reactivation, or actual changes)
    return true;
  }

  /**
   * Determines if the Manage Add-ons button should be shown.
   * Only shown for Active, Trial, or PastDue subscriptions when allowManageAddOns is true.
   */
  canShowManageAddOnsButton(): boolean {
    if (!this.allowManageAddOns || !this.showActionButtons || !this.activeSubscriptionPlan) {
      return false;
    }
    return (
      this.subscriptionStatus === this.SubscriptionStatus.Active ||
      this.subscriptionStatus === this.SubscriptionStatus.Trial ||
      this.subscriptionStatus === this.SubscriptionStatus.PastDue
    );
  }

  /**
   * Checks if subscription exists but is not active (cancelled, expired, suspended)
   */
  isSubscriptionInactive(): boolean {
    return (
      this.activeSubscriptionPlan !== null &&
      (this.subscriptionStatus === this.SubscriptionStatus.Cancelled ||
        this.subscriptionStatus === this.SubscriptionStatus.Expired ||
        this.subscriptionStatus === this.SubscriptionStatus.Suspended)
    );
  }

  /**
   * Gets the message to display for inactive subscriptions
   */
  getInactiveSubscriptionMessage(): string {
    if (this.subscriptionStatus === this.SubscriptionStatus.Cancelled) {
      return 'This subscription has been cancelled. Renew or select a new plan to continue using our services.';
    } else if (this.subscriptionStatus === this.SubscriptionStatus.Expired) {
      return 'This subscription has expired. Please renew or select a new plan to restore access.';
    } else if (this.subscriptionStatus === this.SubscriptionStatus.Suspended) {
      return 'This subscription has been suspended. Please contact support or update your payment method to reactivate.';
    }
    return '';
  }
  calculateTotalSubscriptionPrice(): number {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return 0;

    const basePlanPrice = this.calculateTotalBasePriceOnly();
    const additionalSeatsCost = this.calculateAdditionalSeatsCost();
    const addonsFees = this.calculateTotalAddonFees();

    const subtotal = basePlanPrice + additionalSeatsCost + addonsFees;

    // Apply discount if available
    const savingsInfo = this.calculateDiscountSavings();
    const discountAmount = savingsInfo?.savedAmount || 0;

    return subtotal - discountAmount;
  }

  /**
   * Calculates the base price (sum of all app base prices)
   * Only includes Flat Fee and Per Seat charges from the charges array
   * @returns Total base price
   */
  calculateTotalBasePriceOnly(): number {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return 0;

    // Apply billing cycle multiplier
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    return this.activeSubscriptionPlan.apps.reduce((sum, app) => {
      if (!app.charges || app.charges.length === 0) return sum;

      const enteredSeats = Math.max(0, app.currentSeats - app.includedSeats);

      // Calculate total from charges array - only Flat and PerSeat (if enteredSeats > 0)
      const appTotal = app.charges.reduce((chargeSum: number, charge: any) => {
        const isFlat = charge.type === ChargeType.Flat;
        const isPerSeat = charge.type === ChargeType.PerSeat;

        if (isFlat) {
          return chargeSum + (charge.value || 0) * multiplier;
        } else if (isPerSeat && enteredSeats > 0) {
          // Per Seat: perSeatPrice * enteredSeats * multiplier
          return chargeSum + (charge.value || 0) * enteredSeats * multiplier;
        }
        return chargeSum;
      }, 0);

      return sum + appTotal;
    }, 0);
  }

  /**
   * Calculates the cost of additional seats beyond included seats
   * Note: This method is deprecated - seat costs are now calculated in calculateTotalBasePriceOnly
   * Keeping for backward compatibility but returns 0
   * @returns 0 (seats are calculated in base price from charges)
   */
  calculateAdditionalSeatsCost(): number {
    // Seat costs are now included in calculateTotalBasePriceOnly from charges array
    return 0;
  }

  /**
   * Gets savings information from billing cycle discounts
   * Discount is calculated as a percentage of the subtotal (base price + additional seats + add-ons)
   * @returns Savings info or null if no discount
   */
  calculateDiscountSavings(): { savedAmount: number; savedValue: number; isPercentage: boolean } | null {
    if (!this.activeSubscriptionPlan) return null;

    const pricingTier = this.getCurrentPricingTier();
    if (!pricingTier || !pricingTier.hasDiscount) {
      return null;
    }

    const isPercentage = pricingTier.discountType === DiscountType.Percentage;
    let savedAmount = 0;

    if (isPercentage && pricingTier.discountPercent > 0) {
      // For percentage discounts, apply the percentage to the SUBTOTAL (including add-ons and extra seats)
      const basePlanPrice = this.calculateTotalBasePriceOnly();
      const additionalSeatsCost = this.calculateAdditionalSeatsCost();
      const addonsFees = this.calculateTotalAddonFees();
      const subtotal = basePlanPrice + additionalSeatsCost + addonsFees;
      savedAmount = subtotal * (pricingTier.discountPercent / 100);
    } else if (pricingTier.discountType === DiscountType.Amount && pricingTier.discountAmount > 0) {
      // For fixed amount discounts, use the flat discount amount
      savedAmount = pricingTier.discountAmount;
    } else {
      return null;
    }

    return {
      // Flat dollar amount actually deducted from the total
      savedAmount: savedAmount,
      // Display value: percentage number (e.g. 20) or flat dollar amount (e.g. 10)
      savedValue: isPercentage ? Math.round(pricingTier.discountPercent * 100) / 100 : pricingTier.discountAmount,
      isPercentage,
    };
  }

  /**
   * Gets the current pricing tier based on the billing cycle
   * @returns The current pricing tier or null
   */
  private getCurrentPricingTier(): PricingTier | null {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.pricingTiers) return null;

    switch (this.currentBillingCycle) {
      case BillingCycle.Monthly:
        return this.activeSubscriptionPlan.pricingTiers.monthly;
      case BillingCycle.Yearly_PaidMonthly:
        return this.activeSubscriptionPlan.pricingTiers.yearlyPaidMonthly;
      case BillingCycle.Yearly_PaidYearly:
        return this.activeSubscriptionPlan.pricingTiers.yearlyPaidYearly;
      default:
        return this.activeSubscriptionPlan.pricingTiers.monthly;
    }
  }

  /**
   * Gets a human-readable label for the current billing cycle
   * @returns Billing cycle display name
   */
  getBillingCycleDisplayName(): string {
    switch (this.currentBillingCycle) {
      case BillingCycle.Monthly:
        return 'Monthly';
      case BillingCycle.Yearly_PaidMonthly:
        return 'Yearly (Paid Monthly)';
      case BillingCycle.Yearly_PaidYearly:
        return 'Yearly (Paid Yearly)';
      default:
        return 'Monthly';
    }
  }

  /**
   * Gets the period label for pricing display (/mo or /yr)
   * @returns Period label string
   */
  getBillingPeriodLabel(): string {
    return this.currentBillingCycle === BillingCycle.Yearly_PaidYearly ? '/yr' : '/mo';
  }

  /**
   * Gets the total number of seats across all applications
   * @returns Total seat count
   */
  getTotalSeatsAcrossAllApplications(): number {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return 0;
    return this.activeSubscriptionPlan.apps.reduce((sum, app) => sum + app.currentSeats, 0);
  }

  /**
   * Gets the breakdown of seats by application
   * @returns Array of seat information by app
   */
  getSeatsBreakdownByApplication(): Array<{
    appName: string;
    appId: number;
    includedSeats: number;
    currentSeats: number;
    additionalSeats: number;
  }> {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return [];

    return this.activeSubscriptionPlan.apps.map((app) => ({
      appName: app.appName,
      appId: app.appId,
      includedSeats: app.includedSeats,
      currentSeats: app.currentSeats,
      additionalSeats: Math.max(0, app.currentSeats - app.includedSeats),
    }));
  }

  /**
   * Gets the pricing breakdown by application
   * @returns Array of pricing breakdown by app
   */
  getPricingBreakdownByApplication(): Array<{
    appName: string;
    appId: number;
    basePrice: number;
    additionalSeatsCost: number;
    totalCost: number;
  }> {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return [];

    return this.activeSubscriptionPlan.apps.map((app) => {
      // For revenue sharing apps, additional seat cost is 0 (revenue is calculated separately)
      const additionalSeats = Math.max(0, app.currentSeats - app.includedSeats);
      const additionalSeatsCost = this.isRevenueSharing(app)
        ? 0
        : additionalSeats * app.perSeatPrice;

      return {
        appName: app.appName,
        appId: app.appId,
        basePrice: app.basePrice,
        additionalSeatsCost,
        totalCost: app.basePrice + additionalSeatsCost,
      };
    });
  }

  /**
   * Gets the icon URL for a specific application we need to addd active subs
   * @param appId The application ID
   * @returns The icon URL
   */
  getApplicationIcon(appId: number): string {
    // First try to find the app with icon in the current plan
    if (this.activeSubscriptionPlan?.apps) {
      const app = this.activeSubscriptionPlan.apps.find((a) => a.appId === appId);
      if (app?.appIcon) {
        return app.appIcon;
      }
    }

    // Then try the availableApplications list passed from parent
    if (this.availableApplications && this.availableApplications.length > 0) {
      const availableApp = this.availableApplications.find((a) => a.id === appId);
      if (availableApp?.appIcon) {
        return availableApp.appIcon;
      }
    }

    // Fallback to default icon
    return 'assets/images/navigation/default-app.svg';
  }

  /**
   * Checks if the subscription has any active applications
   * @returns True if there are applications in the plan
   */
  hasActiveApplications(): boolean {
    return !!(
      this.activeSubscriptionPlan &&
      this.activeSubscriptionPlan.apps &&
      this.activeSubscriptionPlan.apps.length > 0
    );
  }

  /**
   * Checks if the subscription has any additional seats beyond included
   * @returns True if there are additional seats
   */
  hasAdditionalSeats(): boolean {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return false;

    return this.activeSubscriptionPlan.apps.some((app) => app.currentSeats > app.includedSeats);
  }

  /**
   * Gets the count of applications in the subscription
   * @returns Number of applications
   */
  getApplicationsCount(): number {
    return this.activeSubscriptionPlan?.apps?.length || 0;
  }

  /**
   * TrackBy function for ngFor optimization
   */
  trackByApplicationId(index: number, app: AppAccessState): number {
    return app.appId;
  }

  /**
   * TrackBy function for detailed charge breakdown
   */
  trackByAppCharges(index: number, appCharges: { appName: string; appId: number }): number {
    return appCharges.appId;
  }

  /**
   * TrackBy function for addons ngFor optimization
   */
  trackByAddonId(index: number, addon: AddonState): number {
    return addon.id;
  }

  /**
   * TrackBy function for detailed addon charge breakdown
   */
  trackByAddonCharges(index: number, addonCharges: { addonName: string; addonId: number }): number {
    return addonCharges.addonId;
  }

  /**
   * Gets detailed charge breakdown by application including all charge types
   * Shows which charges are included in total calculation vs display-only
   */
  getDetailedChargeBreakdownByApplication(): Array<{
    appName: string;
    appId: number;
    charges: Array<{
      type: number;
      typeName: string;
      value: number;
      includedInTotal: boolean;
      perSeatPrice?: number;
      enteredSeats?: number;
      isRevenueShare?: boolean;
    }>;
  }> {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return [];

    // Apply billing cycle multiplier to charge values
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    return this.activeSubscriptionPlan.apps.map((app) => {
      const enteredSeats = Math.max(0, app.currentSeats - app.includedSeats);

      return {
        appName: app.appName,
        appId: app.appId,
        charges: (app.charges || [])
          .map((charge: any) => {
            const isFlat = charge.type === ChargeType.Flat;
            const isPerSeat = charge.type === ChargeType.PerSeat;
            const isRevenueShare = charge.type === ChargeType.PercentageOfRevenue;

            // For Per Seat: only include if enteredSeats > 0
            if (isPerSeat && enteredSeats === 0) {
              return null; // Will be filtered out
            }

            // Calculate value
            let chargeValue = 0;
            if (isFlat) {
              chargeValue = (charge.value || 0) * multiplier;
            } else if (isPerSeat) {
              // Per Seat: perSeatPrice * enteredSeats * multiplier
              chargeValue = (charge.value || 0) * enteredSeats * multiplier;
            } else if (isRevenueShare) {
              // Revenue Share: keep percentage as-is
              chargeValue = charge.value || 0;
            } else {
              // Other charge types (Per Transaction, Per Store)
              chargeValue = (charge.value || 0) * multiplier;
            }

            // Only Flat and PerSeat (with enteredSeats > 0) are included in total
            const includedInTotal = isFlat || (isPerSeat && enteredSeats > 0);
            return {
              type: charge.type,
              typeName: this.getChargeTypeName(charge.type),
              value: chargeValue,
              includedInTotal: includedInTotal,
              perSeatPrice: isPerSeat ? charge.value || 0 : undefined,
              enteredSeats: isPerSeat ? enteredSeats : undefined,
              isRevenueShare: isRevenueShare,
            };
          })
          .filter((charge) => charge !== null) as Array<{
          type: number;
          typeName: string;
          value: number;
          includedInTotal: boolean;
          perSeatPrice?: number;
          enteredSeats?: number;
          isRevenueShare?: boolean;
        }>,
      };
    });
  }

  /**
   * Gets detailed charge breakdown by addon including all charge types
   * Shows which charges are included in total calculation vs display-only
   */
  getDetailedChargeBreakdownByAddon(): Array<{
    addonName: string;
    addonId: number;
    charges: Array<{
      type: number;
      typeName: string;
      value: number;
      includedInTotal: boolean;
      perSeatPrice?: number;
      enteredSeats?: number;
      isRevenueShare?: boolean;
    }>;
  }> {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.addons) return [];

    // Apply billing cycle multiplier to charge values
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    return this.activeSubscriptionPlan.addons.map((addon) => {
      const enteredSeats = Math.max(0, addon.currentSeats - addon.includedSeats);

      return {
        addonName: addon.name,
        addonId: addon.id,
        charges: (addon.charges || [])
          .map((charge: any) => {
            const isFlat = charge.type === ChargeType.Flat;
            const isPerSeat = charge.type === ChargeType.PerSeat;
            const isRevenueShare = charge.type === ChargeType.PercentageOfRevenue;

            // For Per Seat: only include if enteredSeats > 0
            if (isPerSeat && enteredSeats === 0) {
              return null; // Will be filtered out
            }

            // Calculate value
            let chargeValue = 0;
            if (isFlat) {
              chargeValue = (charge.value || 0) * multiplier;
            } else if (isPerSeat) {
              // Per Seat: perSeatPrice * enteredSeats * multiplier
              chargeValue = (charge.value || 0) * enteredSeats * multiplier;
            } else if (isRevenueShare) {
              // Revenue Share: keep percentage as-is
              chargeValue = charge.value || 0;
            } else if (charge.type === ChargeType.PerStore) {
              // Per Store: scale by billing cycle
              chargeValue = (charge.value || 0) * multiplier;
            } else {
              // Other charge types (Per Transaction)
              chargeValue = charge.value || 0;
            }

            // Only Flat and PerSeat (with enteredSeats > 0) are included in total
            const includedInTotal = isFlat || (isPerSeat && enteredSeats > 0);
            return {
              type: charge.type,
              typeName: this.getChargeTypeName(charge.type),
              value: chargeValue,
              includedInTotal: includedInTotal,
              perSeatPrice: isPerSeat ? charge.value || 0 : undefined,
              enteredSeats: isPerSeat ? enteredSeats : undefined,
              isRevenueShare: isRevenueShare,
            };
          })
          .filter((charge) => charge !== null) as Array<{
          type: number;
          typeName: string;
          value: number;
          includedInTotal: boolean;
          perSeatPrice?: number;
          enteredSeats?: number;
          isRevenueShare?: boolean;
        }>,
      };
    });
  }

  /**
   * Gets human-readable charge type name
   */
  getChargeTypeName(chargeType: number): string {
    switch (chargeType) {
      case ChargeType.Flat:
        return 'Flat Fee';
      case ChargeType.PerSeat:
        return 'Per Seat';
      case ChargeType.PerStore:
        return 'Per Store';
      case ChargeType.PercentageOfRevenue:
        return 'Revenue Share';
      case ChargeType.PerTransaction:
        return 'Per Transaction';
      default:
        return 'Other Charge';
    }
  }

  /**
   * Formats charge value based on charge type
   * Revenue Share shows as percentage, others show as currency
   */
  formatChargeValue(value: number, chargeType: number): string {
    if (chargeType === ChargeType.PercentageOfRevenue) {
      return `${value.toFixed(2)}%`;
    }
    return `$${value.toFixed(2)}`;
  }

  /**
   * Gets the total number of extra seats (beyond included) across all apps
   * @returns Total extra seats count
   */
  getTotalExtraSeats(): number {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return 0;
    return this.activeSubscriptionPlan.apps.reduce((sum, app) => {
      return sum + Math.max(0, app.currentSeats - app.includedSeats);
    }, 0);
  }

  /**
   * Calculates total add-on fees
   * Only includes Flat Fee and Per Seat charges from the charges array
   * @returns Total add-on cost
   */
  calculateTotalAddonFees(): number {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.addons) return 0;

    // Apply billing cycle multiplier
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;

    return this.activeSubscriptionPlan.addons.reduce((sum, addon) => {
      if (!addon.charges || addon.charges.length === 0) return sum;

      const enteredSeats = Math.max(0, addon.currentSeats - addon.includedSeats);

      // Calculate total from charges array - only Flat and PerSeat (if enteredSeats > 0)
      const addonTotal = addon.charges.reduce((chargeSum: number, charge: any) => {
        const isFlat = charge.type === ChargeType.Flat;
        const isPerSeat = charge.type === ChargeType.PerSeat;

        if (isFlat) {
          return chargeSum + (charge.value || 0) * multiplier;
        } else if (isPerSeat && enteredSeats > 0) {
          // Per Seat: perSeatPrice * enteredSeats * multiplier
          return chargeSum + (charge.value || 0) * enteredSeats * multiplier;
        }
        return chargeSum;
      }, 0);

      return sum + addonTotal;
    }, 0);
  }

  /**
   * Gets the renewal date (estimated as 30 days from now for demo)
   * In production, this should come from the subscription data
   * @returns Formatted renewal date string
   */
  getRenewalDate(): string {
    const renewalDate = new Date();
    renewalDate.setDate(renewalDate.getDate() + 30);
    return renewalDate.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  }

  /**
   * Gets a short-form renewal date (e.g., "Apr 05, 2026")
   * @returns Short date string
   */
  getShortRenewalDate(): string {
    const renewalDate = new Date();
    renewalDate.setDate(renewalDate.getDate() + 30);
    return renewalDate.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  }

  /**
   * Gets the plan organization/company identifier
   * @returns Organization identifier or 'N/A'
   */
  getPlanOrganization(): string {
    // This should be passed from parent component or fetched from service
    return 'acme-corp';
  }

  /**
   * Gets the plan ID for display
   * @returns Plan ID or code
   */
  getPlanId(): string {
    return this.activeSubscriptionPlan?.id ? `plan-${this.activeSubscriptionPlan.id}` : 'test';
  }

  /**
   * Calculates the percentage saved compared to base price
   * @returns Savings percentage (0-100)
   */
  getSavingsPercentage(): number {
    const savings = this.calculateDiscountSavings();
    if (!savings) return 0;
    // Round to 2 decimal places to avoid long decimal numbers
    return Math.round(savings.savedValue * 100) / 100;
  }

  /**
   * Calculates the subtotal without discount (apps + seats + addons)
   * @returns Subtotal amount
   */
  calculateSubtotal(): number {
    const basePlanPrice = this.calculateTotalBasePriceOnly();
    const additionalSeatsCost = this.calculateAdditionalSeatsCost();
    const addonsFees = this.calculateTotalAddonFees();
    return basePlanPrice + additionalSeatsCost + addonsFees;
  }

  /**
   * Gets the total number of included seats across all apps
   * @returns Total included seats
   */
  getTotalIncludedSeats(): number {
    if (!this.activeSubscriptionPlan || !this.activeSubscriptionPlan.apps) return 0;
    return this.activeSubscriptionPlan.apps.reduce((sum, app) => sum + app.includedSeats, 0);
  }

  /**
   * Checks if an app uses revenue sharing instead of per-seat pricing
   * @param app The application to check
   * @returns True if the app uses revenue sharing
   */
  isRevenueSharing(app: AppAccessState): boolean {
    if (!app.charges || app.charges.length === 0) return false;

    // Check if any charge has type PercentageOfRevenue
    return app.charges.some((charge: any) => charge.type === ChargeType.PercentageOfRevenue);
  }

  /**
   * Checks if an app has per-seat charge
   * @param app The application to check
   * @returns True if the app has per-seat pricing
   */
  hasPerSeatCharge(app: AppAccessState): boolean {
    if (!app.charges || app.charges.length === 0) return false;

    // Check if any charge has type PerSeat
    return app.charges.some((charge: any) => charge.type === ChargeType.PerSeat);
  }

  /**
   * Gets the revenue sharing percentage for an app
   * @param app The application
   * @returns Revenue sharing percentage or null
   */
  getRevenueSharingPercentage(app: AppAccessState): number | null {
    if (!app.charges || app.charges.length === 0) return null;

    const revenueCharge = app.charges.find(
      (charge: any) => charge.type === ChargeType.PercentageOfRevenue,
    );
    const value = revenueCharge?.value || null;
    // Round to 2 decimal places to avoid long decimal numbers
    return value !== null ? Math.round(value * 100) / 100 : null;
  }

  /**
   * Gets addon icon based on addon name or category
   * @param addon The addon
   * @returns SVG icon identifier
   */
  getAddonIconType(addon: AddonState): 'clock' | 'report' | 'default' {
    const name = addon.name.toLowerCase();
    if (name.includes('support') || name.includes('priority')) return 'clock';
    if (name.includes('report') || name.includes('analytics')) return 'report';
    return 'default';
  }

  /**
   * Gets addon description based on addon name
   * @param addon The addon
   * @returns Description string
   */
  getAddonDescription(addon: AddonState): string {
    const name = addon.name.toLowerCase();
    if (name.includes('support') || name.includes('priority')) {
      return '24/7 dedicated support channel & SLA guarantee';
    }
    if (name.includes('report') || name.includes('analytics')) {
      return 'Custom exports, scheduled delivery & deep analytics';
    }
    return 'Enhanced features and capabilities';
  }

  /**
   * Returns ALL charges for an addon as display-ready value/label pairs.
   * Each charge type (Flat, PerSeat, PerTransaction, Revenue) is shown independently.
   */
  getAddonAllChargesForDisplay(addon: AddonState): Array<{ value: string; label: string }> {
    const isYearlyPaidAnnually = this.currentBillingCycle === BillingCycle.Yearly_PaidYearly;
    const multiplier = isYearlyPaidAnnually ? 12 : 1;
    const periodLabel = isYearlyPaidAnnually ? '/yr' : '/mo';

    if (!addon.charges || addon.charges.length === 0) {
      const base = (addon.basePrice || 0) * multiplier;
      return [{ value: `$${base.toFixed(2)}`, label: periodLabel }];
    }

    const result: Array<{ value: string; label: string }> = [];
    for (const c of addon.charges as any[]) {
      const type = Number(c.type);
      const val = c.value || 0;
      if (val === 0) continue;
      switch (type) {
        case ChargeType.Flat:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: periodLabel });
          break;
        case ChargeType.PerSeat:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: '/seat' });
          break;
        case ChargeType.PerStore:
          result.push({ value: `$${(val * multiplier).toFixed(2)}`, label: '/store' });
          break;
        case ChargeType.PerTransaction:
          result.push({ value: `$${val.toFixed(2)}`, label: '/transaction' });
          break;
        case ChargeType.PercentageOfRevenue:
          result.push({ value: `${val.toFixed(2)}%`, label: 'of revenue' });
          break;
        default:
          result.push({ value: `$${val.toFixed(2)}`, label: '' });
      }
    }
    return result.length > 0 ? result : [{ value: '$0.00', label: periodLabel }];
  }

  /**
   * Formats the trial end date as a human-readable string (e.g. "Aug 24, 2025")
   */
  getTrialEndDateFormatted(): string {
    if (!this.trialEndDate) return '';
    const date = new Date(this.trialEndDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * Calculates the number of calendar days remaining in the trial.
   * Returns 0 if the trial has already ended.
   */
  getTrialRemainingDays(): number | null {
    if (!this.trialEndDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(this.trialEndDate);
    end.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }

  /**
   * Returns a grammatically correct countdown label for the trial period.
   * E.g. "Trial ends today", "Trial ends in 1 day", "Trial ends in 3 days"
   */
  getTrialRemainingDaysLabel(): string {
    const days = this.getTrialRemainingDays();
    if (days === null) return '';
    if (days === 0) return 'Trial ends today';
    if (days === 1) return 'Trial ends in 1 day';
    return `Trial ends in ${days} days`;
  }

  /**
   * Gets the display label for subscription status
   * @returns Status label string
   */
  getStatusLabel(): string {
    switch (this.subscriptionStatus) {
      case this.SubscriptionStatus.Active:
        return 'Active';
      case this.SubscriptionStatus.Trial:
        return 'Trial';
      case this.SubscriptionStatus.PastDue:
        return 'Past Due';
      case this.SubscriptionStatus.Suspended:
        return 'Suspended';
      case this.SubscriptionStatus.Expired:
        return 'Expired';
      case this.SubscriptionStatus.Cancelled:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  }
}
